import React, { useState, useMemo } from "react";
import { Modal, ModalHeader } from "../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../globalComponents/ui/Button";
import MultiSelectDropdown from "../../globalComponents/form/dropdowns/MultiSelectDropdown";
import { generateTimePeriodOptions, getStartingMonth } from "../CarbonFootPrinting/utils/PeriodCalculationUtils";

/** Brand accent colour matching the global settings page */
const ACCENT = "#3f88a5";

/**
 * Mapping from aggregation key to display label shown in the filter pill.
 * @type {Record<string, string>}
 */
const AGGREGATION_LABELS = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
  HALF_YEARLY: "Half-Yearly",
  YEARLY: "Yearly",
};

/**
 * Count the number of non-empty advanced filters (used for the badge).
 *
 * @param {string[]} selectedLocations
 * @param {string[]} selectedModules
 * @param {string[]} selectedPeriods
 * @param {string}   aggregation
 * @param {string}   defaultAggregation   - company default; if same as selected it doesn't count as "active"
 * @returns {number}
 */
function countActiveFilters(selectedLocations, selectedModules, selectedPeriods, aggregation, defaultAggregation) {
  let count = 0;
  if (selectedLocations.length > 0) count++;
  if (selectedModules.length > 0) count++;
  if (selectedPeriods.length > 0) count++;
  if (aggregation && aggregation !== defaultAggregation) count++;
  return count;
}

/**
 * Build human-readable period options that embed the start-month context.
 * For Q1 when startingMonth=7 → "Q1 (Jul - Sep)".
 *
 * @param {string} aggregation  - e.g. "QUARTERLY"
 * @param {number} startingMonth - 1-indexed month (e.g. 7 for July)
 * @returns {{ value: string; label: string }[]}
 */
function buildPeriodOptions(aggregation, startingMonth) {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const base = generateTimePeriodOptions(aggregation, startingMonth);

  if (aggregation === "QUARTERLY") {
    return base.map((opt, i) => {
      const startIdx = ((startingMonth - 1) + i * 3) % 12;
      const endIdx = (startIdx + 2) % 12;
      return { value: opt.value, label: `Q${i + 1} (${MONTHS[startIdx]} – ${MONTHS[endIdx]})` };
    });
  }

  if (aggregation === "HALF_YEARLY") {
    return base.map((opt, i) => {
      const startIdx = ((startingMonth - 1) + i * 6) % 12;
      const endIdx = (startIdx + 5) % 12;
      return { value: opt.value, label: `H${i + 1} (${MONTHS[startIdx]} – ${MONTHS[endIdx]})` };
    });
  }

  return base;
}

/* ─────────────────────────────────────────────────────────────── */
/*  Advanced Filters Modal                                         */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Modal that contains all advanced filter controls (Location, Module, Aggregation, Period).
 * Mirrors the "Advanced Filters" modal used in Data Management.
 *
 * @param {object} props
 * @param {boolean}  props.show                  - whether the modal is visible
 * @param {Function} props.onClose               - called to close the modal
 * @param {string[]} props.locationOptions       - [{value, label}] for locations
 * @param {string[]} props.selectedLocations     - currently selected location values
 * @param {any[]}    props.moduleOptions         - [{value, label}] for modules
 * @param {string[]} props.selectedModules       - currently selected module values
 * @param {string}   props.aggregation           - currently selected aggregation ("MONTHLY" etc.)
 * @param {string}   props.defaultAggregation    - company-level default aggregation
 * @param {string[]} props.selectedPeriods       - currently selected period values
 * @param {Function} props.onApply               - (locations, modules, aggregation, periods) => void
 * @param {Function} props.onReset               - reset all advanced filters
 */
const AdvancedFiltersModal = ({
  show,
  onClose,
  locationOptions = [],
  selectedLocations = [],
  moduleOptions = [],
  selectedModules = [],
  aggregation = "MONTHLY",
  defaultAggregation = "MONTHLY",
  selectedPeriods = [],
  onApply,
  onReset,
}) => {
  const startingMonth = getStartingMonth();

  /** Local draft state – only committed on "Apply" */
  const [localLocations, setLocalLocations] = useState(selectedLocations);
  const [localModules, setLocalModules] = useState(selectedModules);
  const [localAggregation, setLocalAggregation] = useState(aggregation);
  const [localPeriods, setLocalPeriods] = useState(selectedPeriods);

  /** Sync draft when modal opens */
  React.useEffect(() => {
    if (show) {
      setLocalLocations(selectedLocations);
      setLocalModules(selectedModules);
      setLocalAggregation(aggregation);
      setLocalPeriods(selectedPeriods);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  /** Recompute period options whenever local aggregation changes */
  const periodOptions = useMemo(
    () => buildPeriodOptions(localAggregation, startingMonth),
    [localAggregation, startingMonth]
  );

  /** When aggregation changes, reset period selections */
  const handleAggregationChange = (value) => {
    setLocalAggregation(value);
    setLocalPeriods([]);
  };

  const activeCount = countActiveFilters(
    localLocations,
    localModules,
    localPeriods,
    localAggregation,
    defaultAggregation
  );

  /** Section label helper */
  const SectionLabel = ({ children }) => (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#64748b",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );

  return (
    <Modal show={show} onClose={onClose} maxWidth={500}>
      <ModalHeader
        icon="fas fa-sliders-h"
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            Dashboard Filters
            {activeCount > 0 && (
              <span
                style={{
                  padding: "2px 8px",
                  background: ACCENT,
                  color: "#fff",
                  borderRadius: 20,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {activeCount} active
              </span>
            )}
          </span>
        }
        subtitle="Narrow down all charts by location, module, and reporting period."
        onClose={onClose}
      />

      <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 22, overflowY: "auto" }}>

        {/* ── Location ── */}
        <div>
          <SectionLabel>Location</SectionLabel>
          <MultiSelectDropdown
            options={locationOptions}
            selected={localLocations}
            onChange={setLocalLocations}
            placeholder="All Locations"
            searchPlaceholder="Search locations…"
          />
        </div>

        {/* ── Module ── */}
        <div>
          <SectionLabel>Module</SectionLabel>
          <MultiSelectDropdown
            options={moduleOptions}
            selected={localModules}
            onChange={setLocalModules}
            placeholder="All Modules"
            searchPlaceholder="Search modules…"
          />
        </div>

        {/* ── Aggregation Type ── */}
        <div>
          <SectionLabel>Aggregation Type</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(AGGREGATION_LABELS).map(([key, label]) => {
              const active = localAggregation === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleAggregationChange(key)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 20,
                    border: `1.5px solid ${active ? ACCENT : "#e2e8f0"}`,
                    background: active ? "#e0f2fe" : "#f8fafc",
                    color: active ? ACCENT : "#64748b",
                    fontWeight: active ? 700 : 500,
                    fontSize: 12,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Period ── */}
        <div>
          <SectionLabel>
            Period&nbsp;
            <span style={{ color: ACCENT, textTransform: "none", letterSpacing: 0 }}>
              ({AGGREGATION_LABELS[localAggregation]})
            </span>
          </SectionLabel>
          <MultiSelectDropdown
            options={periodOptions}
            selected={localPeriods}
            onChange={setLocalPeriods}
            placeholder="All Periods"
            searchPlaceholder="Search periods…"
          />
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 16,
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <SecondaryButton
            type="button"
            onClick={() => {
              onReset?.();
              onClose();
            }}
          >
            Reset All
          </SecondaryButton>
          <div style={{ display: "flex", gap: 8 }}>
            <SecondaryButton type="button" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => {
                onApply?.(localLocations, localModules, localAggregation, localPeriods);
                onClose();
              }}
            >
              Apply Filters
            </PrimaryButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────── */
/*  FilterSection – quick bar + Advanced Filters popup            */
/* ─────────────────────────────────────────────────────────────── */

/**
 * Top-level filter bar for the ESG Progress Dashboard.
 *
 * Renders a compact row with:
 *  - Financial Year single-select (quick filter)
 *  - "Filters" button that opens the AdvancedFiltersModal
 *  - Active-filter badge count
 *  - "Clear" button
 *
 * @param {object}   props
 * @param {any[]}    props.financialYear          - array of { id, financial_year_value } objects
 * @param {string}   props.financialYearId        - currently selected FY id
 * @param {Function} props.onFinancialYearChange  - called on FY select change
 * @param {any[]}    props.locationOptions        - [{value, label}] for locations
 * @param {string[]} props.selectedLocations      - selected location values
 * @param {Function} props.setSelectedLocations
 * @param {any[]}    props.moduleOptions          - [{value, label}] for modules
 * @param {string[]} props.selectedModules        - selected module values
 * @param {Function} props.setSelectedModules
 * @param {string}   props.aggregation            - current aggregation key ("MONTHLY" etc.)
 * @param {Function} props.setAggregation
 * @param {string}   props.defaultAggregation     - company default aggregation
 * @param {string[]} props.selectedPeriods        - selected period values
 * @param {Function} props.setSelectedPeriods
 * @param {Function} props.onClearFilters         - clear all filters
 */
const FilterSection = ({
  financialYear,
  financialYearId,
  onFinancialYearChange,
  locationOptions = [],
  selectedLocations = [],
  setSelectedLocations,
  moduleOptions = [],
  selectedModules = [],
  setSelectedModules,
  aggregation = "MONTHLY",
  setAggregation,
  defaultAggregation = "MONTHLY",
  selectedPeriods = [],
  setSelectedPeriods,
  onClearFilters,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const advancedFilterCount = countActiveFilters(
    selectedLocations,
    selectedModules,
    selectedPeriods,
    aggregation,
    defaultAggregation
  );

  /**
   * Applies filters from the modal draft state to the parent.
   *
   * @param {string[]} locs       - selected location values
   * @param {string[]} mods       - selected module values
   * @param {string}   agg        - aggregation key
   * @param {string[]} periods    - selected period values
   */
  const handleApply = (locs, mods, agg, periods) => {
    setSelectedLocations(locs);
    setSelectedModules(mods);
    setAggregation?.(agg);
    setSelectedPeriods(periods);
  };

  /**
   * Resets all advanced filters and closes the modal.
   */
  const handleReset = () => {
    onClearFilters?.();
  };

  const hasAny = financialYearId || advancedFilterCount > 0;

  return (
    <>
      {/* ── Quick bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "#fff",
          borderRadius: 10,
          padding: "10px 18px",
          marginBottom: 20,
          boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
          flexWrap: "wrap",
        }}
      >
        {/* Left accent bar */}
        <div
          style={{
            width: 3,
            height: 22,
            borderRadius: 2,
            background: ACCENT,
            flexShrink: 0,
          }}
        />

        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#1e293b",
            marginRight: 6,
            whiteSpace: "nowrap",
          }}
        >
          Dashboard Filters
        </span>

        <div style={{ width: 1, height: 22, background: "#e2e8f0", flexShrink: 0 }} />

        {/* Financial Year quick select */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label
            style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}
          >
            FY
          </label>
          <select
            value={financialYearId || ""}
            onChange={onFinancialYearChange}
            style={{
              padding: "5px 10px",
              borderRadius: 8,
              border: `1.5px solid ${financialYearId ? ACCENT : "#e2e8f0"}`,
              background: financialYearId ? "#f0f7ff" : "#fff",
              fontSize: 13,
              color: financialYearId ? "#1e293b" : "#94a3b8",
              outline: "none",
              cursor: "pointer",
              minHeight: 32,
            }}
          >
            <option value="">Select Financial Year</option>
            {Array.isArray(financialYear) &&
              financialYear.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.financial_year_value || item.name || `Year ${item.id}`}
                </option>
              ))}
          </select>
        </div>

        <div style={{ width: 1, height: 22, background: "#e2e8f0", flexShrink: 0 }} />

        {/* Active filter chips summary */}
        {advancedFilterCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {selectedLocations.length > 0 && (
              <span style={{ fontSize: 11, background: "#e0f2fe", color: ACCENT, borderRadius: 12, padding: "2px 8px", fontWeight: 600 }}>
                {selectedLocations.length} location{selectedLocations.length > 1 ? "s" : ""}
              </span>
            )}
            {selectedModules.length > 0 && (
              <span style={{ fontSize: 11, background: "#e0f2fe", color: ACCENT, borderRadius: 12, padding: "2px 8px", fontWeight: 600 }}>
                {selectedModules.length} module{selectedModules.length > 1 ? "s" : ""}
              </span>
            )}
            {aggregation && aggregation !== defaultAggregation && (
              <span style={{ fontSize: 11, background: "#e0f2fe", color: ACCENT, borderRadius: 12, padding: "2px 8px", fontWeight: 600 }}>
                {AGGREGATION_LABELS[aggregation] || aggregation}
              </span>
            )}
            {selectedPeriods.length > 0 && (
              <span style={{ fontSize: 11, background: "#e0f2fe", color: ACCENT, borderRadius: 12, padding: "2px 8px", fontWeight: 600 }}>
                {selectedPeriods.length} period{selectedPeriods.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Advanced Filters button */}
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "6px 14px",
            borderRadius: 8,
            border: `1.5px solid ${advancedFilterCount > 0 ? ACCENT : "#e2e8f0"}`,
            background: advancedFilterCount > 0 ? "#e0f2fe" : "#f8fafc",
            color: advancedFilterCount > 0 ? ACCENT : "#64748b",
            fontWeight: 700,
            fontSize: 12.5,
            cursor: "pointer",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
            height: 34,
          }}
        >
          <i className="fas fa-sliders-h" aria-hidden="true" />
          Filters
          {advancedFilterCount > 0 && (
            <span
              style={{
                background: ACCENT,
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 20,
                lineHeight: "16px",
              }}
            >
              {advancedFilterCount}
            </span>
          )}
        </button>

        {/* Clear all */}
        {hasAny && (
          <button
            type="button"
            onClick={onClearFilters}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "6px 12px",
              border: "1.5px solid #e2e8f0",
              borderRadius: 8,
              background: "transparent",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              color: "#94a3b8",
              height: 34,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = "#64748b"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#94a3b8"; }}
          >
            <i className="fas fa-times" aria-hidden="true" />
            Clear
          </button>
        )}
      </div>

      {/* ── Advanced Filters Modal ── */}
      <AdvancedFiltersModal
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        locationOptions={locationOptions}
        selectedLocations={selectedLocations}
        moduleOptions={moduleOptions}
        selectedModules={selectedModules}
        aggregation={aggregation}
        defaultAggregation={defaultAggregation}
        selectedPeriods={selectedPeriods}
        onApply={handleApply}
        onReset={handleReset}
      />
    </>
  );
};

export default FilterSection;